import React, { useEffect, useState } from "react";
import { Plus, Save, X, Archive, Award } from "lucide-react";
import { CertificateTemplateDialog } from "@/components/CertificateTemplateDialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function WeiterbildungMassnahmenKatalog() {
  const [weiterbildungen, setWeiterbildungen] = useState([]);
  const [edited, setEdited] = useState({});
  const [certModule, setCertModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/weiterbildung/measures");
      setWeiterbildungen(r.data);
      const e = {};
      r.data.forEach((m) => { e[m.measure_id] = m.modules.map((mod) => ({ ...mod })); });
      setEdited(e);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateField = (measureId, idx, field, value) => {
    setEdited((prev) => {
      const modules = [...prev[measureId]];
      modules[idx] = { ...modules[idx], [field]: value };
      return { ...prev, [measureId]: modules };
    });
  };

  const addModule = (measureId) => {
    setEdited((prev) => ({
      ...prev,
      [measureId]: [...prev[measureId], { module_id: null, name: "Neues Modul", ue: 0, price_per_ue: 0, duration: "" }],
    }));
  };

  const removeModule = (measureId, idx) => {
    setEdited((prev) => ({ ...prev, [measureId]: prev[measureId].filter((_, i) => i !== idx) }));
  };

  const save = async (measureId) => {
    const modules = edited[measureId];
    for (const mod of modules) {
      if (!mod.name || !mod.name.trim()) { toast.error("Jedes Modul braucht einen Namen"); return; }
      if (!mod.ue || Number(mod.ue) <= 0) { toast.error(`Modul "${mod.name}": UE muss größer als 0 sein`); return; }
      if (!mod.price_per_ue || Number(mod.price_per_ue) <= 0) { toast.error(`Modul "${mod.name}": Preis/UE muss größer als 0 sein`); return; }
    }
    setSavingId(measureId);
    try {
      await api.patch(`/weiterbildung/measures/${measureId}`, {
        modules: modules.map((m) => ({ module_id: m.module_id, name: m.name, ue: parseFloat(m.ue), price_per_ue: parseFloat(m.price_per_ue), duration: m.duration || null })),
      });
      toast.success("Weiterbildung gespeichert");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSavingId(null); }
  };

  const createWeiterbildung = async () => {
    if (!newName.trim()) { toast.error("Bitte Namen angeben"); return; }
    try {
      await api.post("/weiterbildung/measures", { name: newName.trim(), modules: [] });
      toast.success("Weiterbildung angelegt");
      setNewName(""); setNewOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const archiveOrDelete = async (m) => {
    if (!window.confirm(`Weiterbildung "${m.name}" wirklich entfernen?`)) return;
    try {
      const r = await api.delete(`/weiterbildung/measures/${m.measure_id}`);
      toast.success(r.data.archived ? "Weiterbildung archiviert (wird noch von Teilnehmern genutzt)" : "Weiterbildung gelöscht");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  if (loading) return <div className="text-sm text-slate-500 text-center py-10">Lädt…</div>;

  return (
    <div data-testid="wb-massnahmen-katalog">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <p className="text-sm text-slate-600 max-w-xl">
          Eine Weiterbildung besteht aus mehreren Modulen. UE und Preis/UE je Modul sind FEST (zertifizierte
          Maßnahme) und dürfen pro Teilnehmer nicht verändert werden. In der Kundenakte werden dann nur die
          gebuchten Module angekreuzt — Gesamt-UE und Gesamtpreis ergeben sich automatisch aus der Summe.
          "Dauer" ist optional: ohne Angabe wird die Dauer im Vertrag aus dem Klassen-Zeitraum berechnet.
        </p>
        <button onClick={() => setNewOpen((v) => !v)} className="px-3 py-2 text-xs font-medium bg-slate-900 text-white hover:bg-black flex items-center gap-1.5 shrink-0" data-testid="wb-massnahme-new-btn">
          <Plus size={12} strokeWidth={1.5} /> Neue Weiterbildung
        </button>
      </div>
      {newOpen && (
        <div className="flex gap-2 mb-6 items-end">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="z. B. Digitale Kompetenzen für Kreative"
            className="input-tech text-xs" data-testid="wb-massnahme-new-name-input" />
          <button onClick={createWeiterbildung} className="px-3 py-2 text-xs font-medium bg-slate-900 text-white hover:bg-black" data-testid="wb-massnahme-new-save-btn">Anlegen</button>
          <button onClick={() => setNewOpen(false)} className="px-3 py-2 text-xs font-medium border border-slate-300 hover:bg-slate-100">Abbrechen</button>
        </div>
      )}

      {weiterbildungen.length === 0 && !newOpen && (
        <div className="border border-dashed border-slate-300 p-12 text-center text-slate-400 italic text-sm" data-testid="wb-massnahmen-empty">
          Noch keine Weiterbildungen im Katalog. Lege über „Neue Weiterbildung" die erste an.
        </div>
      )}

      <div className="space-y-6">
        {weiterbildungen.map((m) => {
          const modules = edited[m.measure_id] || [];
          const totalUe = modules.reduce((s, mod) => s + (parseFloat(mod.ue) || 0), 0);
          const totalPrice = modules.reduce((s, mod) => s + (parseFloat(mod.ue) || 0) * (parseFloat(mod.price_per_ue) || 0), 0);
          return (
            <div key={m.measure_id} className="border border-slate-200 bg-white" data-testid={`wb-massnahme-card-${m.measure_id}`}>
              <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color }} />
                  <span className="text-sm font-medium text-slate-900">{m.name}</span>
                  {!m.active && <span className="text-[10px] uppercase font-bold text-slate-400">archiviert</span>}
                </div>
                <div className="flex items-center gap-2">
                  {m.active && (
                    <button onClick={() => save(m.measure_id)} disabled={savingId === m.measure_id}
                      className="px-3 py-1.5 text-xs font-medium bg-[#00CCC9] text-white hover:bg-[#00b3b0] disabled:opacity-50 flex items-center gap-1.5"
                      data-testid={`wb-massnahme-save-${m.measure_id}`}>
                      <Save size={12} strokeWidth={1.5} /> {savingId === m.measure_id ? "Speichert…" : "Speichern"}
                    </button>
                  )}
                  <button onClick={() => archiveOrDelete(m)} className="text-slate-400 hover:text-red-600" data-testid={`wb-massnahme-delete-${m.measure_id}`} title="Entfernen">
                    {m.active ? <X size={14} strokeWidth={1.5} /> : <Archive size={14} strokeWidth={1.5} />}
                  </button>
                </div>
              </div>
              <div className={`overflow-x-auto ${!m.active ? "opacity-50 pointer-events-none" : ""}`}>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-overline text-slate-500 text-left border-b border-slate-100">
                      <th className="px-4 py-2">Modul</th>
                      <th className="px-4 py-2 w-28">UE (fest)</th>
                      <th className="px-4 py-2 w-32">Preis/UE (€)</th>
                      <th className="px-4 py-2 w-28">Dauer (Vertrag)</th>
                      <th className="px-4 py-2 w-28">Gesamt (€)</th>
                      <th className="px-4 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map((mod, idx) => {
                      const total = (parseFloat(mod.ue) || 0) * (parseFloat(mod.price_per_ue) || 0);
                      return (
                        <tr key={mod.module_id || `new-${idx}`} className="border-b border-slate-50" data-testid={`wb-module-row-${m.measure_id}-${idx}`}>
                          <td className="px-4 py-2">
                            <input value={mod.name || ""} onChange={(e) => updateField(m.measure_id, idx, "name", e.target.value)}
                              className="input-tech text-xs w-full" data-testid={`wb-module-name-${m.measure_id}-${idx}`} />
                          </td>
                          <td className="px-4 py-2">
                            <input type="number" min="0" value={mod.ue ?? 0}
                              onChange={(e) => updateField(m.measure_id, idx, "ue", e.target.value)}
                              className="input-tech font-mono text-xs w-24" data-testid={`wb-module-ue-${m.measure_id}-${idx}`} />
                          </td>
                          <td className="px-4 py-2">
                            <input type="number" min="0" step="0.01" value={mod.price_per_ue ?? ""}
                              onChange={(e) => updateField(m.measure_id, idx, "price_per_ue", e.target.value)}
                              placeholder="0,00" className="input-tech font-mono text-xs w-24" data-testid={`wb-module-price-${m.measure_id}-${idx}`} />
                          </td>
                          <td className="px-4 py-2">
                            <input value={mod.duration || ""} onChange={(e) => updateField(m.measure_id, idx, "duration", e.target.value)}
                              placeholder="z. B. 4 Wochen" className="input-tech text-xs w-28" data-testid={`wb-module-duration-${m.measure_id}-${idx}`} />
                          </td>
                          <td className="px-4 py-2 font-mono text-slate-600">{total.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</td>
                          <td className="px-4 py-2">
                            <button onClick={() => removeModule(m.measure_id, idx)} className="text-slate-400 hover:text-red-600" data-testid={`wb-module-remove-${m.measure_id}-${idx}`}>
                              <X size={14} strokeWidth={1.5} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="px-4 py-2.5 border-t border-slate-100 flex flex-wrap items-center gap-2 text-xs" data-testid={`wb-cert-modules-${m.measure_id}`}>
                  <span className="inline-flex items-center gap-1 text-slate-500"><Award size={12} /> Zertifikat:</span>
                  {(m.modules || []).filter((mod) => mod.name).map((mod) => (
                    <button key={mod.module_id || mod.name} onClick={() => setCertModule({ measureId: m.measure_id, module: mod.name })}
                      className="border border-slate-300 px-2 py-1 hover:bg-slate-900 hover:text-white transition-colors" data-testid={`wb-cert-module-btn-${m.measure_id}-${mod.module_id}`}>{mod.name}</button>
                  ))}
                </div>
              </div>
              <div className="px-4 py-3 flex items-center justify-between">
                {m.active && (
                  <button onClick={() => addModule(m.measure_id)} className="text-xs font-medium text-[#0A6E6C] hover:underline flex items-center gap-1" data-testid={`wb-module-add-${m.measure_id}`}>
                    <Plus size={12} strokeWidth={1.5} /> Modul hinzufügen
                  </button>
                )}
                {modules.length > 0 && (
                  <div className="text-xs font-mono text-slate-500" data-testid={`wb-massnahme-totals-${m.measure_id}`}>
                    Gesamt: {totalUe} UE · {totalPrice.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <CertificateTemplateDialog measureId={certModule?.measureId} module={certModule?.module} onClose={() => setCertModule(null)} />
    </div>
  );
}
